import { FilterGroupEnum } from '@honeycomb/data';
// This function tests if there is a difference between two filterState arrays
export const isDifferent = (a, b) => {
    if ((a && !b) || (b && !a))
        return true;
    if (a && b) {
        if (a.length !== b.length)
            return true;
        return a.some((groupA, groupIndex) => {
            const groupB = b[groupIndex];
            if (groupA.id !== groupB.id || groupA.type !== groupB.type || groupA.name !== groupB.name)
                return true;
            if (groupA.type === FilterGroupEnum.Checkbox && groupB.type === FilterGroupEnum.Checkbox) {
                if (groupA.pill !== groupB.pill)
                    return true;
                if (groupA.items.length !== groupB.items.length)
                    return true;
                return groupA.items.some((itemA, itemIndex) => {
                    const itemB = groupB.items[itemIndex];
                    return (itemA.count !== itemB.count ||
                        itemA.label !== itemB.label ||
                        itemA.id !== itemB.id ||
                        itemA.selected !== itemB.selected);
                });
            }
            if ((groupA.type === FilterGroupEnum.Range && groupB.type === FilterGroupEnum.Range) ||
                (groupA.type === FilterGroupEnum.Pricerange && groupB.type === FilterGroupEnum.Pricerange)) {
                return (groupA.formatString !== groupB.formatString ||
                    groupA.min !== groupB.min ||
                    groupA.max !== groupB.max ||
                    groupA.step !== groupB.step ||
                    groupA.lower !== groupB.lower ||
                    groupA.upper !== groupB.upper);
            }
            if (groupA.type === FilterGroupEnum.Text && groupB.type === FilterGroupEnum.Text) {
                return groupA.value !== groupB.value;
            }
            return true;
        });
    }
    return false;
};
// This function tests if there are any active filters applied
export const areFiltersClean = (state) => {
    return state.every((group) => {
        if (group.type === FilterGroupEnum.Checkbox) {
            return group.items.every((item) => item.selected === false);
        }
        if (group.type === FilterGroupEnum.Range || group.type === FilterGroupEnum.Pricerange) {
            return group.lower === group.min && group.upper === group.max;
        }
        if (group.type === FilterGroupEnum.Text) {
            return ['', null, undefined].includes(group.value);
        }
        return false;
    });
};
// This function tests if there no filters to apply
export const areFiltersEmpty = (state) => {
    return state.every((group) => {
        if (group.type === FilterGroupEnum.Checkbox) {
            return group.items.length === 0;
        }
        if (group.type === FilterGroupEnum.Range || group.type === FilterGroupEnum.Pricerange) {
            return group.lower === 0 && group.upper === 0;
        }
        return false;
    });
};
/*  Function to parse a number into a string given a formatString
    Valid substitutions are:
        {r[number of decimal places to be round to (defaults to 0)]} = rounded (e.g. 300.4532 + '£{r2}' => '£300.45'
        {s[num of characters between separators (defaults to 3)][separator character (default to ',')][decimal places (default to 0)]}
            = separated (e.g. 30450.45 + '£{s3!1}' => '£30!450.5')
        {m} = minutes (e.g. 340 + '{h}:{m}' => '05:40')
        {h} = hours (e.g. 340 + '{h}:{m}' => '05:40')
*/
export const applyFormatString = (value, formatString) => {
    if (!formatString) {
        return value.toString();
    }
    const minutes = (value % 60).toString().padStart(2, '0');
    const hours = Math.floor(value / 60)
        .toString()
        .padStart(2, '0');
    const separatorReplacer = (_, amount, delimiter, decimal) => {
        const formattedValue = value.toFixed(decimal || 0).toString();
        // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
        // LookBehind does not work on safari so this regex wont work  -> \\B(?<!\\.\\d*)(?=(\\d{${amount || 3}})+(?!\\d))
        const separatorRegex = `\\B(?=(\\d{${amount || 3}})+(?!\\d))`;
        const parts = formattedValue.split('.');
        parts[0] = parts[0].replace(new RegExp(separatorRegex, 'g'), delimiter || ',');
        return parts.join('.');
    };
    const roundedReplacer = (_, decimal) => {
        return value.toFixed(decimal || 0).toString();
    };
    return formatString
        .replace(/\{s(\d*)(\W?)(\d*)\}/g, separatorReplacer)
        .replace(/\{r(\d*)\}/g, roundedReplacer)
        .replace('{m}', minutes)
        .replace('{h}', hours);
};
export const mapFiltersToState = (filters) => {
    var _a;
    return ((_a = filters === null || filters === void 0 ? void 0 : filters.map((group) => {
        // eslint-disable-next-line no-underscore-dangle
        if (group.__typename === 'CheckboxFilterGroup' && group.id) {
            return Object.assign(Object.assign({}, group), { type: FilterGroupEnum.Checkbox });
        }
        // eslint-disable-next-line no-underscore-dangle
        if (group.__typename === 'RangeFilterGroup' && group.id && group.type === FilterGroupEnum.Pricerange) {
            return Object.assign(Object.assign({}, group), { type: FilterGroupEnum.Pricerange });
        }
        // eslint-disable-next-line no-underscore-dangle
        if (group.__typename === 'RangeFilterGroup' && group.id) {
            return Object.assign(Object.assign({}, group), { type: FilterGroupEnum.Range });
        }
        if (group.__typename === 'TextFilter' && group.id) {
            return Object.assign(Object.assign({}, group), { type: FilterGroupEnum.Text });
        }
        return null;
    }).filter((group) => !!group)) !== null && _a !== void 0 ? _a : []);
};
export const calculateLeadingText = (filterGroup, allFilters, inboundLeadingText) => {
    // Specific case for filter group with id `outboundTakeOffRange`
    if (filterGroup.id === 'outboundTakeOffRange') {
        const departureGroup = allFilters.find((group) => group.id === 'departureAirport');
        // eslint-disable-next-line no-underscore-dangle
        if (departureGroup && departureGroup.__typename === 'CheckboxFilterGroup') {
            const allUnselected = departureGroup.items.every((item) => !item.selected);
            return departureGroup.items
                .filter((item) => allUnselected || item.selected)
                .map((item) => item.label)
                .join(', ');
        }
    }
    // eslint-disable-next-line no-underscore-dangle
    if (filterGroup.id === 'returnTakeOffRange' && filterGroup.__typename === 'RangeFilterGroup') {
        // To rework update the return airport names on filter to match multiple ones
        return inboundLeadingText || null;
    }
    return null;
};
export function toggleArrayValue(value, state) {
    if (state.includes(value)) {
        // Remove the value
        return [...state.filter((n) => n !== value)];
    }
    // Add the value
    return [...state, value];
}
